/**
 * The admin rail's wire vocabulary, as closed sets.
 *
 * The generated schema types these fields as bare `string`/`string[]` because the API declares
 * them as such (they are CHECK-constrained in the database, not enums in the OpenAPI document).
 * That means the compiler cannot tell us when a value is one we have never seen. So every
 * mapping here is written to be TOTAL over the known set and to degrade honestly on anything
 * else — an unknown divert reason renders as itself and disables approve, rather than silently
 * falling into a bucket that lets a reviewer act on a state we do not understand.
 */
import type { components } from '@bymyword/api-types';

type Schemas = components['schemas'];

export type NeedsReviewItem = Schemas['NeedsReviewItemResponse'];
export type DeadLetter = Schemas['DeadLetterResponse'];
export type Suppression = Schemas['SuppressionResponse'];
export type AdminRequest = Schemas['AdminRequestResponse'];
export type AdminUser = Schemas['AdminUserResponse'];
export type ReferralFraudFlag = Schemas['ReferralFraudFlagResponse'];
export type VelocityFlag = Schemas['VelocityFlagResponse'];
export type InviteBurstFlag = Schemas['InviteBurstFlagResponse'];
export type RefundAbuseFlag = Schemas['RefundAbuseFlagResponse'];
export type LinkedAccount = Schemas['AdminLinkedAccountResponse'];
export type LinkedAccounts = Schemas['AdminLinkedAccountsResponse'];
export type StaffTokens = Schemas['StaffTokenResponse'];
export type AdminUsageDashboard = Schemas['AdminUsageDashboard'];
export type AdminUsagePeriod = Schemas['AdminUsagePeriod'];
export type AdminAiOperationMetrics = Schemas['AdminAiOperationMetrics'];

/**
 * The five reasons the pipeline diverts a reference into `needs_review`.
 * Source of truth: AdminReviewEndpoints.NeedsReviewItemResponse's doc comment.
 */
export const DIVERT_REASONS = [
  'moderation_flagged',
  'moderation_unusable_response',
  'extract_unusable_response',
  'no_key_points',
  'draft_unusable_response',
] as const;

export type DivertReason = (typeof DIVERT_REASONS)[number];

export function isDivertReason(value: string | null): value is DivertReason {
  return value !== null && (DIVERT_REASONS as readonly string[]).includes(value);
}

/**
 * The moderation pass's category codes. Only `moderation_flagged` carries these — the other four
 * reasons are pipeline failures a human has to look at, not content judgements.
 *
 * These are NEVER shown to the referee (the reject email is deliberately generic). They exist
 * here only so a reviewer knows what the model objected to.
 */
export const MODERATION_CATEGORIES = [
  'abuse',
  'protected_attribute',
  'defamatory',
  'off_topic',
  'other',
] as const;

export type ModerationCategory = (typeof MODERATION_CATEGORIES)[number];

const DIVERT_REASON_COPY: Record<DivertReason, { label: string; detail: string }> = {
  moderation_flagged: {
    label: 'Moderation flagged',
    detail:
      'The moderation pass objected to something in the recording. The categories below say what kind of objection it was — not what was said.',
  },
  moderation_unusable_response: {
    label: 'Moderation call failed',
    detail:
      'The moderation provider returned something we could not read. This is our failure, not the referee’s — approving retries the call.',
  },
  extract_unusable_response: {
    label: 'Key-point extraction failed',
    detail:
      'The extraction provider returned something we could not read. Approving retries the call.',
  },
  draft_unusable_response: {
    label: 'Drafting failed',
    detail:
      'The drafting provider returned something we could not read. Approving retries the call.',
  },
  no_key_points: {
    label: 'Nothing to draft from',
    detail:
      'The recording yielded no key points, so there is nothing to ground a letter on. Approving is not possible — asking for another take is the only way forward.',
  },
};

/** Human copy for a divert reason. Unknown values render as themselves rather than a guess. */
export function divertReasonCopy(reason: string | null): { label: string; detail: string } {
  if (isDivertReason(reason)) return DIVERT_REASON_COPY[reason];
  if (reason === null) {
    return {
      label: 'Reason not recorded',
      detail:
        'This request is in review without a recorded reason. Treat it as unrecognised: approving is disabled until someone establishes why it diverted.',
    };
  }
  return {
    label: reason,
    detail:
      'We do not recognise this divert reason. Approving is disabled — it would re-drive a pipeline for a state this console does not understand.',
  };
}

const MODERATION_CATEGORY_LABELS: Record<ModerationCategory, string> = {
  abuse: 'Abuse',
  protected_attribute: 'Protected attribute',
  defamatory: 'Defamatory',
  off_topic: 'Off topic',
  other: 'Other',
};

/** Human label for a moderation category; unknown codes render verbatim. */
export function moderationCategoryLabel(category: string): string {
  return (MODERATION_CATEGORY_LABELS as Record<string, string>)[category] ?? category;
}

/**
 * Whether approve is a meaningful action for a divert reason.
 *
 * `no_key_points` → the API answers 409 `nothing_to_draft`, because there is nothing to ground a
 * letter on. The other four re-drive the pipeline. An unknown reason is NOT approvable either:
 * re-driving a pipeline for a state we cannot name is exactly the kind of thing that should
 * require a code change, not a click.
 *
 * The server enforces this regardless — this function exists so the reviewer is told why the
 * action is unavailable instead of being handed a raw 409 after the fact.
 */
export function canApprove(divertReason: string | null): boolean {
  return isDivertReason(divertReason) && divertReason !== 'no_key_points';
}

/** Why approve is unavailable, for the reviewer. Null when it IS available. */
export function approveBlockedReason(divertReason: string | null): string | null {
  if (canApprove(divertReason)) return null;
  if (divertReason === 'no_key_points') {
    return 'There are no key points to draft a letter from. Ask for another take instead.';
  }
  return 'This divert reason is not one this console can approve. Ask for another take, or escalate.';
}

/** Absolute, unambiguous timestamp. Back-office readers need the real time, not "3 days ago". */
export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-NZ', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** How long something has been waiting, in coarse, honest units. */
export function formatWaiting(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hr`;
  return `${Math.floor(hours / 24)} days`;
}
